import { Injectable } from '@angular/core';
import { map, of, tap } from 'rxjs';

import { ApiService } from '../api/api.service';
import { ApiUserPermissions } from '../models/Permissions.model';
import { ApiUser } from '../models/Profile.model';
import { StateService } from '../state/state.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  constructor(
    private api: ApiService,
    private state: StateService,
  ) {}

  permissions() {
    if (this.state.get('permissions').length) {
      return this.state.select<string[]>('permissions');
    }

    return this.api.post<ApiUserPermissions>('permissions/user').pipe(
      tap((res) => {
        if (res.success) {
          return this.state.set({ permissions: res.permissions });
        }
        return this.state.set({ permissions: [] });
      }),
      map((res) => res.permissions),
    );
  }

  hasPermission(name: string | string[], op = 'or') {
    //#region Valid Permissions Array
    const valid = [
      'OWNER',
      'ADMIN',
      'LICENSES.VIEW',
      'CONFIGS.VIEW',
      'CONFIGS.WRITE',
      'LOGS.VIEW',
      'IDENTITIES.VIEW',
      'APOLLO.VIEW',
      'APOLLO.WRITE',
      'IPAUTH.VIEW',
      'IPAUTH.WRITE',
      'WEBHOOKS.VIEW',
      'WEBHOOKS.WRITE',
      'TRIGGERS.VIEW',
      'TRIGGERS.WRITE',
      'THREATS.VIEW',
      'THREATS.ADD',
      'DOCS.VIEW',
      'USERS.VIEW',
      'USERS.WRITE',
      'WHITELIST.VIEW',
      'WHITELIST.WRITE',
      'THREATS.EDIT',
      'THREATS.REMOVE',
      'AUDIT.VIEW',
    ];
    //#endregion
    if (typeof name === 'string') {
      if (!valid.includes(name)) {
        console.error(`[FAC] Invalid permission: ${name}`);
        return of(false);
      }
      return this.permissions().pipe(
        map((perms) => {
          return (
            perms?.includes(name) ||
            (name != 'OWNER' && perms?.includes('ADMIN'))
          );
        }),
      );
    }
    if (Array.isArray(name)) {
      if (!name.every((n) => valid.includes(n))) {
        console.error(
          `[FAC] Invalid permission: ${name.filter((n) => !valid.includes(n)).join(', ')}`,
        );
        return of(false);
      }
      return this.permissions().pipe(
        map((perms) => {
          return op === 'or'
            ? name.some(
                (n) =>
                  perms?.includes(n) ||
                  (n != 'OWNER' && perms?.includes('ADMIN')),
              )
            : name.every(
                (n) =>
                  perms?.includes(n) ||
                  (n != 'OWNER' && perms?.includes('ADMIN')),
              );
        }),
      );
    }
    return of(false);
  }

  hasGlobalPermission(name: string) {
    return this.state.select<ApiUser>('user').pipe(
      map((user) => {
        return !user
          ? false
          : user.permissions.includes(name) ||
              user.permissions.includes('ADMIN');
      }),
    );
  }
}
